import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Phone, Mail, Clock, ShieldCheck, CheckCircle2 } from "lucide-react";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/Breadcrumbs";
import AnimatedTitle from "../components/AnimatedTitle";
import { CONTACT, SERVICES, INQUIRY_TYPES, INSURANCES, IN_PERSON_NOTE } from "../data/site";
import { submitInquiry } from "../lib/api";
import { breadcrumbSchema } from "../lib/schema";

const EMPTY = {
  inquiry_type: INQUIRY_TYPES[0],
  full_name: "",
  email: "",
  phone: "",
  service: "",
  insurance: "",
  preferred_time: "",
  referrer_name: "",
  referrer_organization: "",
  message: "",
};

export default function Contact() {
  const [params] = useSearchParams();
  const preService = SERVICES.find((s) => s.slug === params.get("service"));
  const [data, setData] = useState(EMPTY);
  // Prefill from ?service= after mount (keeps prerendered HTML hydration-safe).
  useEffect(() => {
    setData((d) => ({
      ...d,
      service: preService ? preService.title : d.service,
    }));
  }, [preService]);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const isReferral = data.inquiry_type === "Professional referral";

  const onChange = (e) => setData((d) => ({ ...d, [e.target.name]: e.target.value }));
  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await submitInquiry(data, e.target.elements["bot-field"]?.value || "");
      setSent(true);
      setData(EMPTY);
    } catch {
      setError("Something went wrong. Please call us or try again.");
    } finally {
      setLoading(false);
    }
  };

  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Contact", path: "/contact" },
  ];

  return (
    <>
      <SEO
        title="Contact Us | Book an Online Therapy Session in NJ"
        description="Book an online therapy session anywhere in New Jersey. Call (973) 358-2121 or send an inquiry. Professional referrals welcome."
        path="/contact"
        schema={breadcrumbSchema(crumbs)}
      />

      <section className="pt-10 pb-20 md:pt-14" data-testid="contact-page">
        <div className="container-site">
          <Breadcrumbs items={crumbs} />
          <div className="mt-6 grid lg:grid-cols-12 gap-10 lg:gap-14">
            <div className="lg:col-span-5">
              <span className="eyebrow">Contact</span>
              <AnimatedTitle text="Book a" accent="session" testid="page-title" className="mt-3 text-[2.5rem] md:text-6xl leading-[1.04] tracking-[-0.025em]" />
              <p className="mt-5 text-lg text-body leading-relaxed">
                Tell us what you're looking for. We'll reply within one business day to verify insurance and schedule your first session.
              </p>

              <ul className="mt-8 space-y-5">
                <InfoRow icon={Phone} label="Call">
                  <a href={CONTACT.phoneHref} className="font-semibold text-[var(--ink)] hover:text-[var(--sage)]" data-testid="contact-page-phone">
                    {CONTACT.phone}
                  </a>
                </InfoRow>
                <InfoRow icon={Mail} label="Email">
                  <a href={CONTACT.emailHref} className="font-semibold text-[var(--ink)] hover:text-[var(--sage)] break-all" data-testid="contact-page-email">
                    {CONTACT.email}
                  </a>
                </InfoRow>
                <InfoRow icon={Clock} label="Hours">
                  {CONTACT.hours.map((h) => (
                    <div key={h.day} className="text-sm text-[var(--ink)]">
                      {h.day}: {h.time}
                    </div>
                  ))}
                </InfoRow>
                <InfoRow icon={ShieldCheck} label="Online only">
                  <div className="text-sm text-[var(--ink)]">Secure video sessions for anyone located in New Jersey.</div>
                  <div className="mt-1 text-xs text-body" data-testid="contact-inperson-note">{IN_PERSON_NOTE}</div>
                </InfoRow>
              </ul>

              <div className="mt-8 rounded-2xl bg-[var(--sage-tint)] p-5 text-sm text-[var(--ink)] leading-relaxed">
                <strong>For providers & schools:</strong> choose “Professional referral” to refer a client. We'll follow up promptly.
              </div>
            </div>

            <div className="lg:col-span-7">
              {sent ? (
                <div className="card p-10 text-center" data-testid="contact-success">
                  <CheckCircle2 className="h-12 w-12 mx-auto text-[var(--sage)]" />
                  <h2 className="mt-5 text-2xl">Thank you — we received your inquiry.</h2>
                  <p className="mt-3 text-body">A member of our team will reach out within one business day.</p>
                  <button type="button" className="mt-6 btn btn-secondary" onClick={() => setSent(false)} data-testid="contact-send-another">
                    Send another inquiry
                  </button>
                </div>
              ) : (
                <form
                  name="inquiry"
                  method="POST"
                  onSubmit={onSubmit}
                  className="card p-6 md:p-8 space-y-5"
                  data-testid="inquiry-form"
                >
                  <input type="hidden" name="form-name" value="inquiry" />
                  <p className="hidden">
                    <label>
                      Don't fill this out: <input name="bot-field" />
                    </label>
                  </p>

                  <div>
                    <span className="label">I'm reaching out about</span>
                    <div className="grid sm:grid-cols-2 gap-2" role="radiogroup">
                      {INQUIRY_TYPES.map((t) => (
                        <label
                          key={t}
                          className={`cursor-pointer rounded-xl border px-4 py-3 text-sm font-medium transition ${
                            data.inquiry_type === t
                              ? "border-[var(--sage)] bg-[var(--sage-tint)] text-[var(--ink)]"
                              : "border-[var(--line)] text-body hover:border-[#cfd6c8]"
                          }`}
                          data-testid={`inquiry-type-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        >
                          <input type="radio" name="inquiry_type" value={t} checked={data.inquiry_type === t} onChange={onChange} className="sr-only" />
                          {t}
                        </label>
                      ))}
                    </div>
                  </div>

                  {isReferral && (
                    <div className="grid sm:grid-cols-2 gap-4" data-testid="referral-fields">
                      <Field label="Your name (referring professional)" name="referrer_name" value={data.referrer_name} onChange={onChange} required />
                      <Field label="Practice / organization" name="referrer_organization" value={data.referrer_organization} onChange={onChange} />
                    </div>
                  )}

                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label={isReferral ? "Client full name" : "Full name"} name="full_name" value={data.full_name} onChange={onChange} required />
                    <Field label="Email" type="email" name="email" value={data.email} onChange={onChange} required />
                    <Field label="Phone" type="tel" name="phone" value={data.phone} onChange={onChange} required />
                    <Select label="Service" name="service" value={data.service} onChange={onChange}>
                      <option value="">Not sure yet</option>
                      {SERVICES.map((s) => (
                        <option key={s.slug} value={s.title}>{s.title}</option>
                      ))}
                    </Select>
                    <Select label="Insurance" name="insurance" value={data.insurance} onChange={onChange}>
                      <option value="">Select (optional)</option>
                      {INSURANCES.filter((i) => i !== "And more").map((i) => (
                        <option key={i} value={i}>{i}</option>
                      ))}
                      <option value="Other">Other</option>
                      <option value="Private pay">Private pay</option>
                    </Select>
                    <Field label="Preferred days / times" name="preferred_time" value={data.preferred_time} onChange={onChange} placeholder="e.g. Weekday evenings" />
                  </div>
                  <div>
                    <label className="label" htmlFor="message">Anything you'd like us to know? (optional)</label>
                    <textarea id="message" name="message" rows={4} value={data.message} onChange={onChange} className="field resize-none" data-testid="inquiry-message" />
                  </div>
                  <p className="text-xs text-body leading-relaxed">
                    Please don't include detailed health information — this form isn't HIPAA-secure. In crisis? Call or text 988, or call 911.
                  </p>
                  {error && (
                    <p role="alert" className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="inquiry-error">
                      {error}
                    </p>
                  )}
                  <button type="submit" disabled={loading} className="btn btn-primary w-full disabled:opacity-60" data-testid="inquiry-submit">
                    {loading ? "Sending…" : "Request my session"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function InfoRow({ icon: Icon, label, children }) {
  return (
    <li className="flex gap-4">
      <span className="grid h-10 w-10 place-items-center rounded-xl bg-[var(--sage-tint)] text-[var(--sage)] shrink-0">
        <Icon className="h-5 w-5" />
      </span>
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.12em] text-body">{label}</div>
        <div className="mt-1">{children}</div>
      </div>
    </li>
  );
}

function Field({ label, name, type = "text", ...rest }) {
  return (
    <div>
      <label className="label" htmlFor={name}>
        {label} {rest.required && <span className="text-[var(--sage)]">*</span>}
      </label>
      <input id={name} name={name} type={type} className="field" data-testid={`inquiry-${name}`} {...rest} />
    </div>
  );
}

function Select({ label, name, children, ...rest }) {
  return (
    <div>
      <label className="label" htmlFor={name}>
        {label} {rest.required && <span className="text-[var(--sage)]">*</span>}
      </label>
      <select id={name} name={name} className="field appearance-none bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2212%22 height=%2212%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%235D6F59%22 stroke-width=%222%22><path d=%22m6 9 6 6 6-6%22/></svg>')] bg-no-repeat bg-[right_1rem_center] pr-10" data-testid={`inquiry-${name}`} {...rest}>
        {children}
      </select>
    </div>
  );
}
